import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { Loader2, CheckCircle2, XCircle, Shield, FolderOpen, AlertTriangle } from "lucide-react";
import { useFacilities } from "@/hooks/useFacilities";

interface TestResult {
  success: boolean;
  accountInfo?: {
    name?: string;
    email?: string;
  };
  inboxStatus?: {
    accessible: boolean;
    fileCount?: number;
    error?: string;
  };
  error?: string;
  details?: {
    suggestion?: string;
  };
}

interface FacilityTestResult extends TestResult {
  facilityCode: string;
  facilityPath: string;
}

export function DropboxConfigForm() {
  const { data: facilities = [] } = useFacilities();
  // Only facilities Documents actually monitors (have an inbox path configured)
  const monitoredFacilities = facilities.filter((f) => f.dropbox_inbox_path);

  const [testStatus, setTestStatus] = useState<"idle" | "testing" | "success" | "error" | "not_configured">("idle");
  const [testResults, setTestResults] = useState<FacilityTestResult[]>([]);
  // Populated only when the mutation itself throws (e.g. a network failure)
  // before any per-facility result comes back -- testResults stays [] in
  // that case, so the error card falls back to this instead of rendering
  // nothing.
  const [testError, setTestError] = useState<string | null>(null);

  const testConnection = useMutation({
    mutationFn: async () => {
      setTestStatus("testing");

      // Test each monitored inbox — same Dropbox account, but each folder's
      // accessibility is checked independently.
      const results = await Promise.all(
        monitoredFacilities.map(async (facility) => {
          const { data, error } = await supabase.functions.invoke("test-dropbox-connection", {
            body: { inboxPath: facility.dropbox_inbox_path },
          });
          if (error) throw error;
          return { ...(data as TestResult), facilityCode: facility.code, facilityPath: facility.dropbox_inbox_path! };
        })
      );
      return results;
    },
    onSuccess: (results) => {
      setTestResults(results);
      setTestError(null);
      const notConfigured = results.some((r) => r.error?.includes("not configured"));
      const allOk = results.every((r) => r.success);
      if (allOk) {
        setTestStatus("success");
        toast({
          title: "Connection successful",
          description: `Connected as ${results[0]?.accountInfo?.name || results[0]?.accountInfo?.email}`,
        });
      } else if (notConfigured) {
        setTestStatus("not_configured");
      } else {
        setTestStatus("error");
        toast({
          title: "Connection failed",
          description: results.find((r) => !r.success)?.error || "Unknown error",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      setTestStatus("error");
      setTestResults([]);
      setTestError(error.message);
      toast({
        title: "Connection test failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onTestConnection = () => {
    testConnection.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5" />
              Dropbox
            </CardTitle>
            <CardDescription>
              Automatic document processing from Dropbox
            </CardDescription>
          </div>
          {testStatus === "success" && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4 text-green-600" />
              <span>Connected</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          {monitoredFacilities.map((facility) => (
            <div key={facility.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-2 text-sm">
                <FolderOpen className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{facility.code} monitored folder:</span>
                <code className="font-mono text-foreground">{facility.dropbox_inbox_path}</code>
              </div>
            </div>
          ))}
        </div>

        <Button
          variant="outline"
          onClick={onTestConnection}
          disabled={testConnection.isPending || monitoredFacilities.length === 0}
        >
          {testConnection.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Testing...
            </>
          ) : (
            "Test Connection"
          )}
        </Button>

        {/* Connection Result Display */}
        {testStatus === "success" && testResults.length > 0 && (
          <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
              <CheckCircle2 className="h-4 w-4" />
              <span className="font-medium">Connected to Dropbox</span>
            </div>
            <div className="mt-2 text-sm text-green-600 dark:text-green-400 space-y-1">
              <p>Account: {testResults[0]?.accountInfo?.name}</p>
              {testResults[0]?.accountInfo?.email && <p>Email: {testResults[0].accountInfo.email}</p>}
              {testResults.map((r) => (
                <p key={r.facilityCode}>
                  {r.facilityCode} folder: {r.inboxStatus?.accessible ? (
                    <span className="text-green-600">Accessible</span>
                  ) : (
                    <span className="text-amber-600">
                      Not accessible - {r.inboxStatus?.error}
                    </span>
                  )}
                </p>
              ))}
            </div>
          </div>
        )}

        {testStatus === "not_configured" && (
          <div className="p-4 bg-amber-50 dark:bg-amber-950 rounded-lg border border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-medium">Dropbox Not Fully Configured</span>
            </div>
            {/* Per-facility breakdown -- a mixed result (e.g. GWH connected,
                RP not set up yet) shouldn't read as "nothing works". */}
            <div className="mt-2 text-sm space-y-1">
              {testResults.map((r) => (
                <p key={r.facilityCode}>
                  <span className="text-amber-700 dark:text-amber-300">{r.facilityCode}:</span>{" "}
                  {r.success ? (
                    <span className="text-green-600">Connected</span>
                  ) : r.error?.includes("not configured") ? (
                    <span className="text-amber-600">Not configured</span>
                  ) : (
                    <span className="text-red-600">{r.error || "Error"}</span>
                  )}
                </p>
              ))}
            </div>
            <p className="mt-2 text-xs text-amber-500 dark:text-amber-500">
              Contact your administrator to finish setting up Dropbox for any facility marked "Not configured".
            </p>
          </div>
        )}

        {testStatus === "error" && (
          <div className="p-4 bg-red-50 dark:bg-red-950 rounded-lg border border-red-200 dark:border-red-800">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
              <XCircle className="h-4 w-4" />
              <span className="font-medium">Connection Failed</span>
            </div>
            {testResults.length > 0 ? (
              <>
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                  {testResults.find((r) => !r.success)?.error}
                </p>
                {testResults.find((r) => !r.success)?.details?.suggestion && (
                  <p className="mt-1 text-sm text-red-500 dark:text-red-500">
                    {testResults.find((r) => !r.success)?.details?.suggestion}
                  </p>
                )}
              </>
            ) : (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                {testError || "Unknown error"}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
